import { FC, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Avatar, Flex, Layout, Menu, Typography } from 'antd';
import dayjs from 'dayjs';
import { fetchOrganizationById } from 'services/api/organizations';
import { OrganizationDetail } from 'types/api/organizations';

import { Loading, PageWrapper } from 'components/ui';
import { StyledLayout } from './styled';
import { getFileUrl } from 'utils/file';
import { AppointmentsSettings, Contact, UISettings } from './components';
import Pricing from './components/Pricing';

const { Content, Sider } = Layout;

const OrganizationSettings: FC = () => {
  const params = useParams();
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<OrganizationDetail | null>(
    null
  );

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const id = params.id;
    if (!id) return;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchOrganizationById(id);
        if (!cancelled) setOrganization(res);
      } catch {
        // swallow — empty sidebar on failure
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [params.id]);

  const renderContent = () => {
    switch (params.page) {
      case 'contact':
        return <Contact id={params.id as string} />;
      case 'ui':
        return <UISettings id={params.id as string} />;
      case 'appointments':
        return <AppointmentsSettings id={params.id as string} />;
      case 'pricing':
        return <Pricing id={params.id as string} />;
      default:
        return null;
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <PageWrapper
      back
      onClickBack={() => navigate('/organizations')}
      breadcrumbs={{
        items: [
          {
            title: <Link to="/organizations">Organizations</Link>
          },
          {
            title: 'Settings'
          }
        ]
      }}
    >
      <StyledLayout>
        <Sider width={340}>
          <Flex align="center" gap={16} style={{ marginBottom: 32 }}>
            <Avatar src={getFileUrl(organization?.image)} size={60} />
            <div>
              <Typography.Title level={5} style={{ margin: 0 }}>
                {organization?.name?.en ?? ''}
              </Typography.Title>
              <Typography.Paragraph style={{ margin: 0 }}>
                Created At:{' '}
                {dayjs(organization?.created_at).format('DD-MM-YYYY')}
              </Typography.Paragraph>
            </div>
          </Flex>
          <Menu
            selectedKeys={[`${params.page}`]}
            items={[
              {
                key: 'contact',
                label: (
                  <Link to={`/organizations/settings/${params.id}/contact`}>
                    Contact
                  </Link>
                )
              },
              {
                key: 'ui',
                label: (
                  <Link to={`/organizations/settings/${params.id}/ui`}>UI</Link>
                )
              },
              {
                key: 'appointments',
                label: (
                  <Link
                    to={`/organizations/settings/${params.id}/appointments`}
                  >
                    Appointments
                  </Link>
                )
              },
              {
                key: 'pricing',
                label: (
                  <Link to={`/organizations/settings/${params.id}/pricing`}>
                    Pricing
                  </Link>
                )
              }
            ]}
          />
        </Sider>
        <Content>{renderContent()}</Content>
      </StyledLayout>
    </PageWrapper>
  );
};

export default OrganizationSettings;
